import { reportStyles } from './moduleReportStyle';

export const generateReportHTML = ({
  logo,
  userName,
  numModules,
  finishedModules,
  pendingModules,
  progressPercent,
  modulesData,
}) => {
  const truncateText = (text, maxLength = 90, longWordLimit = 20) => {
    const words = text
      .split(' ')
      .map((word) => (word.length > longWordLimit
        ? `${word.slice(0, longWordLimit)}...`
        : word));

    const truncatedText = words.join(' ');
    return truncatedText.length > maxLength
      ? `${truncatedText.slice(0, maxLength)}...`
      : truncatedText;
  };

  const FOOTNOTE_MARKERS = ['¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
  const footnoteEntries = [];
  const rowFootnoteMarker = new Map();

  modulesData.data.forEach((module, rowIndex) => {
    const comment = module?.grading?.adminOverride?.comment?.trim();
    if (!comment) return;
    const marker = FOOTNOTE_MARKERS[footnoteEntries.length] ?? String(footnoteEntries.length + 1);
    footnoteEntries.push({ marker, comment });
    rowFootnoteMarker.set(rowIndex, marker);
  });

  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Helper function to generate a table row
  const generateTableRow = (module, rowIndex) => {
    const gradeValue = module.grading?.grade;
    const footnoteMarker = rowFootnoteMarker.get(rowIndex);
    const gradeDisplay = gradeValue
      ? `${gradeValue}${footnoteMarker || ''}`
      : '-';
    return `
    <tr class="table-row">
      <td class="course-cell">${truncateText(module.course.title)}</td>
      <td class="module-cell">${truncateText(module.module.title)}</td>
      <td class="date-cell">${
  module.correctionDate
    ? new Date(module.correctionDate).toLocaleDateString()
    : 'Not Graded'
}</td>
      <td class="date-cell">${
  module.correctionDuedate
    ? new Date(module.correctionDuedate).toLocaleDateString()
    : ''
}</td>
      <td class="corrector-cell">${
  module?.corrector?.lastname || ''
}</td>
      <td class="grade-cell ${gradeValue ? 'has-grade' : ''}">${gradeDisplay}</td>
    </tr>
  `;
  };

  const generateFootnotesSection = () => {
    if (footnoteEntries.length === 0) return '';
    return `
      <div class="grade-footnotes" style="margin: 24px 40px; font-size: 12px; color: #444;">
        ${footnoteEntries.map(({ marker, comment }) => (
    `<p class="grade-footnote" style="margin: 4px 0;">${marker} Administratively recorded grade: ${escapeHtml(comment)}</p>`
  )).join('')}
      </div>
    `;
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const createHeader = (showLogo = true) => `
    <div class="page-header main-header">
      ${
  showLogo
    ? `<img src="${logo || ''}" alt="Company Logo" class="main-logo"/>`
    : ''
}
      <div class="header-date">${currentDate}</div>
    </div>
  `;

  // Function to create table header row
  const createTableHeader = () => `
    <tr class="table-header">
      <th>Course</th>
      <th>Module</th>
      <th>Correction Date</th>
      <th>Due Date</th>
      <th>Corrector</th>
      <th>Grade</th>
    </tr>
  `;

  const PAGE_HEIGHT = 1123;
  const HEADER_HEIGHT = 100;
  const SUMMARY_SECTION_HEIGHT = 400;
  const ROW_HEIGHT = 90;
  const MARGIN = 40;

  const FIRST_PAGE_AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - SUMMARY_SECTION_HEIGHT - 2 * MARGIN;
  const SUBSEQUENT_PAGE_AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - 2 * MARGIN;

  const FIRST_PAGE_ROWS = Math.floor(FIRST_PAGE_AVAILABLE_HEIGHT / ROW_HEIGHT);
  const ROWS_PER_PAGE = Math.floor(
    SUBSEQUENT_PAGE_AVAILABLE_HEIGHT / ROW_HEIGHT,
  );
  const generateTableContent = () => {
    let content = '';
    const totalRows = modulesData.data.length;
    let currentRow = 0;

    // Calculate if this will be a single page
    const isSinglePage = totalRows <= FIRST_PAGE_ROWS;

    // First page rows
    content += `<div class="page first-page${
      isSinglePage ? ' last-page' : ''
    }">`;
    content += createHeader();
    content += `
      <div class="summary-section">
        <h2 class="report-title">Student Progress Report</h2>
        <div class="summary-grid">
          <div class="summary-card">
            <h4>Student Name</h4>
            <p>${userName}</p>
          </div>
          <div class="summary-card">
            <h4>Total Modules</h4>
            <p>${numModules}</p>
          </div>
        </div>

        <div class="progress-section">
          <h4>Overall Progress</h4>
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${progressPercent}%;"></div>
          </div>
          <p class="progress-text">
            ${finishedModules} out of ${numModules} modules completed
            (${pendingModules} awaiting grading)
          </p>
        </div>
      </div>
    `;

    // Start table on first page
    content += '<div class="table-container">';
    content += '<table>';
    content += createTableHeader();

    // Add rows that fit on first page
    const firstPageRows = Math.min(FIRST_PAGE_ROWS, totalRows);
    for (let i = 0; i < firstPageRows; i += 1) {
      content += generateTableRow(modulesData.data[i], i);
      currentRow += 1;
    }
    content += '</table></div></div>';

    if (!isSinglePage) {
      let pageCount = 1;

      while (currentRow < totalRows) {
        const remainingRows = totalRows - currentRow;

        if (remainingRows > 0) {
          const rowsToAdd = Math.min(ROWS_PER_PAGE, remainingRows);
          const isLastPage = currentRow + rowsToAdd >= totalRows;
          pageCount += 1;
          content += `<div class="page${
            isLastPage ? ' last-page' : ''
          }" data-page="${pageCount}">`;
          content += createHeader();
          content += '<div class="table-container">';
          content += '<table>';
          content += createTableHeader();

          let rowsAdded = 0;
          while (rowsAdded < rowsToAdd && currentRow < totalRows) {
            content += generateTableRow(modulesData.data[currentRow], currentRow);
            currentRow += 1;
            rowsAdded += 1;
          }

          content += '</table></div></div>';

          if (isLastPage) break;
        } else {
          break;
        }
      }
    }
    return content;
  };

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          ${reportStyles}
        </style>
      </head>
      <body>
        ${generateTableContent()}
        ${generateFootnotesSection()}
      </body>
    </html>
  `;
};
